import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Key } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Alert, Button, Input, Space, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { LeftOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useAuthSession } from '@/lib/session'
import { useTrialBalanceAvailability } from '@/features/compliance/hooks'
import type { TrialBalancePlatform } from '@/features/compliance/types'
import { getFeatureThemeClassName, getFeatureThemeStyle, productThemes } from '@/lib/productBranding'
import { OrgJobsExpandedPanel } from './components'

export default function ComplianceOrgListPage() {
  const session = useAuthSession()
  const clientId = session?.currentClientId || ''
  const [nameFilter, setNameFilter] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const featureThemeStyle = getFeatureThemeStyle(productThemes.complianceIntel)
  const expandedRowKeysRef = useRef<string[]>([])
  expandedRowKeysRef.current = expandedRowKeys

  const query = useTrialBalanceAvailability(clientId)

  const data = useMemo(() => {
    let rows = query.data || []
    if (nameFilter.trim()) {
      const q = nameFilter.trim().toLowerCase()
      rows = rows.filter((row) => row.name.toLowerCase().includes(q))
    }
    return rows
  }, [nameFilter, query.data])

  const orgFromUrl = searchParams.get('org')

  useEffect(() => {
    if (!orgFromUrl) return
    if (data.some((p) => p.platformId === orgFromUrl)) {
      setExpandedRowKeys([orgFromUrl])
    }
  }, [orgFromUrl, data])

  useEffect(() => {
    setExpandedRowKeys((keys) => keys.filter((k) => data.some((p) => p.platformId === k)))
  }, [data])

  useEffect(() => {
    if (orgFromUrl && !data.some((p) => p.platformId === orgFromUrl)) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          p.delete('org')
          return p
        },
        { replace: true }
      )
    }
  }, [data, orgFromUrl, setSearchParams])

  const syncUrlToExpansion = useCallback(
    (keys: readonly Key[]) => {
      const strKeys = keys.map((k) => String(k))
      let nextKeys = strKeys
      if (strKeys.length > 1) {
        const prev = expandedRowKeysRef.current
        const added = strKeys.filter((k) => !prev.includes(k))
        const pick = added.length ? added[added.length - 1] : strKeys[strKeys.length - 1]
        nextKeys = pick != null ? [pick] : []
      }
      setExpandedRowKeys(nextKeys)
      const next = nextKeys.length === 1 ? nextKeys[0] : null
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (next) p.set('org', next)
          else p.delete('org')
          return p
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const columns: ColumnsType<TrialBalancePlatform> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => <span className="font-medium text-gray-900">{name}</span>,
    },
    {
      title: 'Available Financial Year Data',
      dataIndex: 'availableDates',
      key: 'availableDates',
      render: (dates: string[]) => (Array.isArray(dates) && dates.length ? dates.join(', ') : '—'),
    },
  ]

  return (
    <FeatureThemeProvider featureKey="complianceIntel" withAntd>
    <div className={`feature-shell ${getFeatureThemeClassName('complianceIntel')}`} style={featureThemeStyle}>
      <div className="feature-shell__header px-6 pt-5 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Link
              to="/clients"
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              aria-label="Back to clients"
            >
              <LeftOutlined />
            </Link>
            <div>
              <h1 className="font-display text-2xl font-bold text-gray-900">CompVerify</h1>
              <p className="text-sm text-gray-500 mt-1">
                Expand an organisation to view and manage CompVerify jobs
              </p>
            </div>
          </div>
          <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => void query.refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      {!clientId && (
        <div className="p-4">
          <Alert type="info" showIcon message="Select a client to view organisations" />
        </div>
      )}

      {clientId && query.isError && (
        <div className="p-4">
          <Alert
            type="error"
            showIcon
            message={(query.error as Error)?.message || 'Failed to load organisations'}
          />
        </div>
      )}

      {clientId && (
        <>
          <div className="p-4 bg-white/70">
            <Space wrap>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Filter by name"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                style={{ width: 220 }}
              />
            </Space>
          </div>

          <div className="p-4">
            <Table<TrialBalancePlatform>
              rowKey="platformId"
              loading={query.isLoading || query.isFetching}
              columns={columns}
              dataSource={data}
              pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '25', '50'] }}
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: syncUrlToExpansion,
                expandedRowRender: (record) => (
                  <OrgJobsExpandedPanel
                    org={record}
                    clientId={clientId}
                    isExpanded={expandedRowKeys.includes(record.platformId)}
                  />
                ),
              }}
            />
          </div>
        </>
      )}
    </div>
    </FeatureThemeProvider>
  )
}
